/* eslint-disable @typescript-eslint/naming-convention */
import React, { useLayoutEffect, useRef, useState } from 'react';

function toLocal(rect: DOMRect, container: HTMLElement) {
  const c = container.getBoundingClientRect();
  return {
    x: rect.left - c.left,
    y: rect.top - c.top,
    w: rect.width,
    h: rect.height,
    cx: rect.left - c.left + rect.width / 2,
    cy: rect.top - c.top + rect.height / 2,
    leftCenter: {
      x: rect.left - c.left,
      y: rect.top - c.top + rect.height / 2,
    },
    rightCenter: {
      x: rect.left - c.left + rect.width,
      y: rect.top - c.top + rect.height / 2,
    },
  };
}

export const OurServicesSection: React.FC = () => {
  const containerRef = useRef<HTMLDivElement>(null);
  const centerRef = useRef<HTMLDivElement>(null);
  const dataRef = useRef<HTMLDivElement>(null);
  const reportRef = useRef<HTMLDivElement>(null);
  const customRef = useRef<HTMLDivElement>(null);
  const [coords, setCoords] = useState<any>({});

  useLayoutEffect(() => {
    const cont = containerRef.current;
    if (!cont) return;

    const measure = () => {
      const getBox = (el?: HTMLElement | null) => {
        if (!el) return {};
        const r = el.getBoundingClientRect();
        return toLocal(r, cont);
      };
      const rect = cont.getBoundingClientRect();
      setCoords({
        center: getBox(centerRef.current),
        data: getBox(dataRef.current),
        report: getBox(reportRef.current),
        custom: getBox(customRef.current),
        w: rect.width,
        h: rect.height,
      });
    };

    measure();
    const observer = new ResizeObserver(measure);
    observer.observe(cont);
    window.addEventListener('resize', measure);
    return () => {
      observer.disconnect();
      window.removeEventListener('resize', measure);
    };
  }, []);

  const makeStraightLine = (from: any, to: any) => {
    if (!from || !to) return '';
    return `M ${from.cx},${from.cy} L ${to.x},${to.y}`;
  };

  return (
    <section
      id="services"
      className="relative bg-gradient-to-b from-[#041421] via-[#03010E] to-[#020812] pt-32 pb-52 overflow-hidden border-t-2 border-[#00D4FF]/50"
    >
      <style>{`
        @keyframes softPulse {
          0%, 100% {
            opacity: 0.5;
            filter: drop-shadow(0 0 8px rgba(0,212,255,0.5));
          }
          50% {
            opacity: 1;
            filter: drop-shadow(0 0 16px rgba(0,212,255,1));
          }
        }
        .pulse-dot {
          animation: softPulse 4s ease-in-out infinite;
        }
        .glow-card {
          background: rgba(255,255,255,0.05);
          backdrop-filter: blur(12px);
          border: 1px solid rgba(255,255,255,0.1);
          border-radius: 1rem;
          transition: all 0.4s ease;
        }
        .glow-card:hover {
          transform: translateY(-4px) scale(1.02);
          box-shadow: 0 0 35px rgba(0,212,255,0.4);
        }
      `}</style>

      {/* 🌌 Background gradient & glow */}
      <div className="absolute inset-0 bg-gradient-to-b from-[#041421] via-[#03010E] to-[#020812] opacity-90" />
      <div className="absolute inset-0 bg-[radial-gradient(circle_at_50%_30%,rgba(0,212,255,0.15),transparent_60%)]" />

      <div ref={containerRef} className="relative container mx-auto px-6 md:px-12 text-white">
        <h2 className="text-3xl md:text-5xl font-extrabold text-center text-[#00D4FF] mb-3">
          OUR SERVICES
        </h2>
        <p className="text-[#9EE7FF] text-lg md:text-xl text-center opacity-80 mb-24">
          Empowering occupational hygiene with innovation, precision, and reliability.
        </p>

        {/* --- SVG lines connecting dynamically --- */}
        <div className="absolute inset-0 z-0 pointer-events-none">
          <svg
            className="absolute inset-0 w-full h-full"
            viewBox={`0 0 ${coords.w || 1000} ${coords.h || 800}`}
            preserveAspectRatio="none"
          >
            <defs>
              <radialGradient id="hubGlow" cx="50%" cy="50%" r="50%">
                <stop offset="0%" stopColor="#00D4FF" stopOpacity="0.4" />
                <stop offset="100%" stopColor="#03010E" stopOpacity="0" />
              </radialGradient>
            </defs>

            {coords.center && (
              <circle
                cx={coords.center.cx}
                cy={coords.center.cy}
                r="100"
                fill="url(#hubGlow)"
                opacity="0.8"
              />
            )}

            <path
              d={makeStraightLine(coords.center, coords.data?.rightCenter)}
              stroke="#00D4FF"
              strokeWidth="2.5"
              fill="none"
              opacity="0.9"
            />
            <path
              d={makeStraightLine(coords.center, coords.report?.leftCenter)}
              stroke="#00D4FF"
              strokeWidth="2.5"
              fill="none"
              opacity="0.9"
            />
            <path
              d={makeStraightLine(coords.center, coords.custom?.leftCenter)}
              stroke="#00D4FF"
              strokeWidth="2.5"
              fill="none"
              opacity="0.9"
            />

            {coords.center && (
              <circle
                cx={coords.center.cx}
                cy={coords.center.cy}
                r="10"
                fill="#00D4FF"
                className="pulse-dot"
              />
            )}
          </svg>
        </div>

        {/* --- Floating dots above boxes --- */}
        <div className="absolute inset-0 pointer-events-none z-20">
          {[coords.data?.rightCenter, coords.report?.leftCenter, coords.custom?.leftCenter].map(
            (p, i) =>
              p && (
                <div
                  key={i}
                  className="absolute w-3 h-3 rounded-full bg-[#00D4FF]"
                  style={{
                    left: `${p.x - 6}px`,
                    top: `${p.y - 6}px`,
                    boxShadow: '0 0 15px rgba(0,212,255,0.9)',
                  }}
                />
              ),
          )}
        </div>

        <div
          ref={centerRef}
          className="absolute left-1/2 top-[45%] -translate-x-1/2 -translate-y-1/2 w-4 h-4"
        />

        {/* --- Layout --- */}
        <div className="grid md:grid-cols-2 gap-20 relative z-10">
          <div ref={dataRef} className="relative glow-card p-8 md:ml-auto">
            <h3 className="text-xl md:text-2xl font-bold mb-3 text-white">DATA CAPTURING</h3>
            <p className="text-[#C7E6F5] leading-relaxed">
              Accurate and efficient data entry tailored to Occupational Hygiene, ensuring reliable
              results and smoother compliance.
            </p>
          </div>

          <div ref={reportRef} className="relative glow-card p-8 md:mr-auto">
            <h3 className="text-xl md:text-2xl font-bold mb-3 text-white">REPORT WRITING</h3>
            <p className="text-[#C7E6F5] leading-relaxed">
              Structured professional reports that deliver clarity, insight, and actionable outcomes
              using custom templates and field data.
            </p>
          </div>
        </div>

        <div className="mt-20 flex justify-center relative z-10">
          <div ref={customRef} className="relative glow-card p-8 md:w-[60%]">
            <h3 className="text-xl md:text-2xl font-bold mb-3 text-white">CUSTOMIZED SOLUTIONS</h3>
            <p className="text-[#C7E6F5] leading-relaxed">
              Innovative tools tailored to your operational needs — from intelligent templates to
              workflow and system enhancements that elevate performance.
            </p>
          </div>
        </div>
      </div>

      {/* 🌊 FLIPPED SMOOTH WAVE TRANSITION (upward) */}
      <div className="absolute bottom-0 left-0 w-full overflow-hidden leading-[0] z-20 rotate-180">
        <svg
          className="relative block w-full h-[120px]"
          xmlns="http://www.w3.org/2000/svg"
          viewBox="0 0 1200 120"
          preserveAspectRatio="none"
        >
          <path
            d="M0,0V46.29c47.29,22.2,104.47,41.85,180,49.81,156.08,16.34,241.19-30.49,411.72-33.77C795.43,59.6,882.39,88.73,1040,106.25c87.44,9.83,150.61-3.88,160-7.15V0Z"
            fill="#03010E"
            opacity="1"
          ></path>
          <path
            d="M0,0V15.81C47.42,34.89,104.56,52.6,180,60.73c156.08,16.34,241.19-30.49,411.72-33.77C795.43,22.52,882.39,51.65,1040,69.17c87.44,9.83,150.61-3.88,160-7.15V0Z"
            fill="#041421"
            opacity="0.95"
          ></path>
          <path
            d="M0,0V5.63C47.42,24.71,104.56,42.43,180,50.55c156.08,16.34,241.19-30.49,411.72-33.77C795.43,12.34,882.39,41.47,1040,59c87.44,9.83,150.61-3.88,160-7.15V0Z"
            fill="#00D4FF"
            opacity="0.15"
          ></path>
        </svg>
      </div>
    </section>
  );
};

export default OurServicesSection;
